<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2><i class="fas fa-file-alt"></i> Mail Documents - #{{ mail.displayId }}</h2>
        <button class="close-btn" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <!-- Attachments Sidebar -->
        <div class="attachments-sidebar">
          <div class="attachments-header">
            <h3>
              <i class="fas fa-paperclip"></i>
              Attachments
              <span class="attachments-count">
                {{ mail.attachments?.length || 0 }}
              </span>
            </h3>
          </div>
          <div class="attachments-list">
            <!-- Fixed ESLint error: moved v-if outside of loop -->
            <template v-if="mail.attachments && mail.attachments.length > 0">
              <div v-for="(attachment, index) in mail.attachments" 
                   :key="index"
                   class="attachment-item"
                   :class="{ 'active': selectedAttachment === attachment }"
                   @click="selectAttachment(attachment)">
                <div class="attachment-item-header">
                  <i :class="getFileIcon(attachment)"></i>
                  <div class="attachment-details">
                    <span class="attachment-name">{{ attachment.originalName }}</span>
                    <span class="attachment-info">
                      <i class="fas fa-file"></i>
                      {{ getFileType(attachment) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="no-attachments">
              <i class="fas fa-file-alt"></i>
              <p>No attachments available</p>
            </div>
          </div>
        </div>

        <!-- Preview Main Content -->
        <div class="preview-main">
          <div v-if="selectedAttachment" class="preview-container">
            <div class="preview-toolbar">
              <div class="preview-title">
                <i :class="getFileIcon(selectedAttachment)"></i>
                {{ selectedAttachment.originalName }}
              </div>
              <div class="preview-actions">
                <button v-if="isImage(selectedAttachment)" 
                        class="preview-btn" 
                        @click="zoomIn" 
                        title="Zoom In">
                  <i class="fas fa-search-plus"></i>
                </button>
                <button v-if="isImage(selectedAttachment)" 
                        class="preview-btn" 
                        @click="zoomOut" 
                        title="Zoom Out">
                  <i class="fas fa-search-minus"></i>
                </button>
                <button v-if="isImage(selectedAttachment)" 
                        class="preview-btn" 
                        @click="resetZoom" 
                        title="Reset Zoom">
                  <i class="fas fa-undo"></i>
                </button>
                <button class="preview-btn download"
                        @click="downloadAttachment(selectedAttachment)">
                  <i class="fas fa-download"></i>
                  Download
                </button>
              </div>
            </div>
            <div class="preview-content">
              <img v-if="isImage(selectedAttachment)" 
                   :src="getFileUrl(selectedAttachment.path)" 
                   :style="{ transform: `scale(${zoomLevel})` }"
                   class="preview-image"
                   @load="onImageLoad"
                   @error="onPreviewError" />
              
              <iframe v-else-if="isPDF(selectedAttachment)"
                      :src="getFileUrl(selectedAttachment.path)"
                      class="preview-pdf"
                      @load="onPdfLoad"
                      @error="onPreviewError"></iframe>
              
              <div v-else class="preview-unsupported">
                <i :class="getFileIcon(selectedAttachment)"></i>
                <p>This file type cannot be previewed</p>
                <button class="preview-btn download" @click="downloadAttachment(selectedAttachment)">
                  <i class="fas fa-download"></i>
                  Download to View
                </button>
              </div>
            </div>
          </div>
          <div v-else class="preview-placeholder">
            <i class="fas fa-file-alt"></i>
            <p>Select a file to preview</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import incomingMailService from '../services/incomingMailService';

export default {
  name: 'PreviewModal',
  props: {
    mail: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const selectedAttachment = ref(null);
    const zoomLevel = ref(1);
    const loading = ref(false);
    const error = ref(null);

    const getFileIcon = (attachment) => {
      if (isImage(attachment)) return 'fas fa-file-image';
      if (isPDF(attachment)) return 'fas fa-file-pdf';
      return 'fas fa-file';
    };

    const getFileType = (attachment) => {
      return attachment.originalName.split('.').pop().toUpperCase();
    };

    const isImage = (attachment) => {
      return attachment.mimetype?.startsWith('image/');
    };

    const isPDF = (attachment) => {
      return attachment.mimetype === 'application/pdf';
    };

    const getFileUrl = (path) => {
      const normalizedPath = path.replace(/\\/g, '/');
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:5001';
      const uploadsBaseUrl = baseUrl.replace(/\/api$/, '');
      const relativePath = normalizedPath.split('Files/').pop();
      return `${uploadsBaseUrl}/files/${relativePath}`;
    };

    const selectAttachment = (attachment) => {
      selectedAttachment.value = attachment;
      resetZoom();
    };

    const zoomIn = () => {
      zoomLevel.value = Math.min(zoomLevel.value + 0.2, 3);
    };

    const zoomOut = () => {
      zoomLevel.value = Math.max(zoomLevel.value - 0.2, 0.5);
    };

    const resetZoom = () => {
      zoomLevel.value = 1;
    };

    const downloadAttachment = async (attachment) => {
      try {
        loading.value = true;
        error.value = null;
        await incomingMailService.downloadAttachment(attachment.path);
      } catch (err) {
        error.value = 'Failed to download attachment';
        console.error('Download error:', err);
      } finally {
        loading.value = false;
      }
    };

    const onImageLoad = () => {
      loading.value = false;
      error.value = null;
    };

    const onPdfLoad = () => {
      loading.value = false;
      error.value = null;
    };

    const onPreviewError = () => {
      loading.value = false;
      error.value = 'Failed to load preview';
    };

    const closeModal = () => {
      emit('close');
    };

    return {
      selectedAttachment,
      zoomLevel,
      loading,
      error,
      getFileIcon,
      getFileType,
      isImage,
      isPDF,
      getFileUrl,
      selectAttachment,
      zoomIn,
      zoomOut,
      resetZoom,
      downloadAttachment,
      onImageLoad,
      onPdfLoad,
      onPreviewError,
      closeModal
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 95%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
}

.modal-header h2 i {
  color: #6b7280;
}

.close-btn {
  background: #f3f4f6;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e5e7eb;
  color: #111827;
}

.modal-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.attachments-sidebar {
  width: 320px;
  border-right: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.attachments-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.attachments-header h3 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.attachments-count {
  background: #e5e7eb;
  color: #4b5563;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
}

.attachments-list {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.attachment-item {
  padding: 0.75rem;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.attachment-item:hover {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.attachment-item.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.attachment-item-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.attachment-item-header i {
  font-size: 1.25rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.attachment-details {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.preview-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.preview-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.preview-toolbar {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #374151;
}

.preview-title i {
  color: #6b7280;
}

.preview-actions {
  display: flex;
  gap: 0.5rem;
}

.preview-btn {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.preview-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.preview-btn.download {
  background: #3b82f6;
  color: #fff;
  border: none;
}

.preview-btn.download:hover {
  background: #2563eb;
}

.preview-content {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f8fafc;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s;
}

.preview-pdf {
  width: 100%;
  height: 100%;
  border: none;
}

.preview-unsupported,
.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #6b7280;
  padding: 2rem;
  text-align: center;
}

.preview-unsupported i,
.preview-placeholder i {
  font-size: 3rem;
  color: #9ca3af;
}

.preview-unsupported p,
.preview-placeholder p {
  margin: 0;
  font-size: 1.1rem;
}

.no-attachments {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
  text-align: center;
}

.no-attachments i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.no-attachments p {
  margin: 0;
  font-size: 0.95rem;
}

@media (max-width: 768px) {
  .modal-content {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .attachments-sidebar {
    width: 280px;
  }
}
</style>
